import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { http, getCustomerToken } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { Sparkles, Zap, Copy, Check, Mail, Lock } from 'lucide-react';

const custCfg = () => ({ headers: { Authorization: `Bearer ${getCustomerToken()}` } });

const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000) % 24;
  const m = Math.floor(diff / 60000) % 60;
  const s = Math.floor(diff / 1000) % 60;
  const cells = [[d, 'Days'], [h, 'Hours'], [m, 'Mins'], [s, 'Secs']];
  return (
    <div className="flex justify-center gap-3 sm:gap-5" data-testid="bf-countdown">
      {cells.map(([v, l]) => (
        <div key={l} className="w-[72px] sm:w-[88px] border border-alp-gold/40 rounded-lg bg-black/60 backdrop-blur py-3 sm:py-4">
          <p className="font-serif text-3xl sm:text-4xl text-alp-gold text-center tabular-nums">{pad(v)}</p>
          <p className="text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-alp-gray text-center mt-1">{l}</p>
        </div>
      ))}
    </div>
  );
}

export default function BlackFriday() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => {
    http.get('/blackfriday/status').then((r) => setStatus(r.data)).catch(() => {});
    if (getCustomerToken()) http.get('/blackfriday/me', custCfg()).then((r) => setMe(r.data)).catch(() => setMe({ signedUp: false }));
    else setMe({ signedUp: false });
  };
  useEffect(() => { load(); }, []);

  const shareUrl = useMemo(() => `${window.location.origin}/black-friday`, []);

  const signup = async () => {
    setBusy(true);
    try {
      const { data } = await http.post('/blackfriday/signup', {}, custCfg());
      setMe({ signedUp: true, rank: data.rank, earlyBird: data.earlyBird });
      load();
      toast({ title: data.earlyBird ? `You're in — Early Bird spot #${data.rank} secured! ⚡` : `You're on the list — #${data.rank}` });
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Please sign in to join the list', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (e) { toast({ title: shareUrl }); }
  };

  if (!status || !me) return <div className="min-h-[60vh]" />;
  const ebTotal = status.earlyBirdLimit || 0;
  const ebTaken = ebTotal - (status.earlyBirdRemaining || 0);

  return (
    <div className="relative min-h-screen bg-alp-black overflow-hidden" data-testid="blackfriday-page">
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(201,162,75,0.14), transparent 70%)' }} />
      <div className="relative max-w-3xl mx-auto px-6 pt-20 pb-28 text-center">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-[11px] tracking-[0.5em] uppercase text-alp-gold mb-4 flex items-center justify-center gap-2">
            <Sparkles size={13} /> Members Only <Sparkles size={13} />
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-alp-cream leading-tight">
            BLACK <span className="text-alp-gold">FRIDAY</span>
          </h1>
          <p className="text-alp-gray text-base mt-4 max-w-xl mx-auto">
            One secret code. Yours alone. Join the list and your personal single-use discount lands in your inbox the moment we go live — gone by Sunday 8pm.
          </p>
        </motion.div>

        {!status.live && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.6 }} className="mt-10">
            <p className="text-[10px] tracking-[0.3em] uppercase text-alp-gray mb-4">The vault opens in</p>
            <Countdown target={status.launchAt} />
          </motion.div>
        )}

        {status.live && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mt-10 border border-alp-gold rounded-lg bg-alp-gold/10 px-6 py-5 inline-block" data-testid="bf-live-banner">
            <p className="font-serif text-2xl text-alp-gold">IT'S LIVE 🖤</p>
            <p className="text-alp-cream text-sm mt-1">{me.signedUp ? 'Your personal code is in your inbox — it expires Sunday.' : 'Codes have been sent to the list.'}</p>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }} className="mt-12">
          {ebTotal > 0 && !status.live && (
            <div className="max-w-md mx-auto mb-8" data-testid="bf-earlybird-race">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-alp-gold flex items-center gap-1.5"><Zap size={13} /> Early Bird — bigger discount</span>
                <span className="text-alp-gray">{status.earlyBirdRemaining > 0 ? `${status.earlyBirdRemaining} spot${status.earlyBirdRemaining === 1 ? '' : 's'} left` : 'All claimed'}</span>
              </div>
              <div className="h-2 rounded-full bg-alp-card border border-alp-line overflow-hidden">
                <div className="h-full bg-gradient-to-r from-alp-gold/60 to-alp-gold transition-all duration-700" style={{ width: `${Math.min(100, (ebTaken / ebTotal) * 100)}%` }} />
              </div>
              <p className="text-[11px] text-alp-gray mt-2">The first {ebTotal} on the list unlock a boosted code. {status.earlyBirdRemaining > 0 ? 'Move fast.' : 'All claimed — standard codes still waiting.'}</p>
            </div>
          )}

          {!me.signedUp ? (
            <button onClick={signup} disabled={busy} data-testid="bf-signup-btn"
              className="btn-glow inline-flex items-center gap-2 bg-alp-gold text-alp-black px-10 py-4 text-xs tracking-[0.25em] uppercase font-semibold rounded hover:bg-alp-goldlight transition-colors disabled:opacity-50">
              <Mail size={15} /> {busy ? 'Securing your spot…' : 'Lock in my secret code'}
            </button>
          ) : (
            <div className="max-w-md mx-auto border border-alp-gold/50 rounded-lg bg-alp-card/60 p-6" data-testid="bf-signedup-card">
              <p className="font-serif text-xl text-alp-cream flex items-center justify-center gap-2"><Check size={18} className="text-alp-gold" /> You're on the list — #{me.rank}</p>
              {me.earlyBird && <p className="text-alp-gold text-sm mt-2 flex items-center justify-center gap-1"><Zap size={13} /> Early Bird spot secured — your code gets the boost</p>}
              <p className="text-alp-gray text-sm mt-3">{status.live ? 'Check your inbox — your code expires Sunday 8pm.' : 'Your personal code will land in your inbox the second we go live.'}</p>
              <div className="mt-5 pt-4 border-t border-alp-line/50">
                <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gray mb-2">Know someone who'd love this?</p>
                <button onClick={copyShare} data-testid="bf-share-btn"
                  className="inline-flex items-center gap-2 border border-alp-gold text-alp-gold px-5 py-2.5 rounded text-xs tracking-[0.15em] uppercase hover:bg-alp-gold hover:text-alp-black transition-colors">
                  {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Link copied' : 'Copy invite link'}
                </button>
              </div>
            </div>
          )}

          <p className="text-alp-gray text-xs mt-8 flex items-center justify-center gap-1.5">
            <Lock size={11} /> One code per member · single use · expires Sunday
          </p>
        </motion.div>
      </div>
    </div>
  );
}
